<script setup lang="ts">
import { computed, PropType } from "vue";
import { Pool } from "@/models/pool.model";
import { getRename } from "@/shared/utils/renames";
import {
  formatProbeValue,
  getProbeLabel,
  getProbeType,
  isProbeHiddenForHivernage,
  shouldHideProbeValueUnit,
} from "@/shared/utils/probes";
import { getActiveMode } from "@/shared/utils/outs";
import OnOffStatus from "@/features/out/OnOffStatus.vue";
import { isHivernage } from "@/shared/utils/pool";
import { usePool } from "@/features/pool/usePool";

const props = defineProps({
  out: { type: Object as PropType<Pool["outs"][number]>, required: false },
  probe: { type: Object as PropType<Pool["probes"][number]>, required: true },
  span: { type: Number, required: true },
});

const pool = usePool();
const unavailable = computed(() => props.probe.filteredValue === -1000);

const label = computed(
  () =>
    getRename(pool, 2, props.probe.index) ?? getProbeLabel(props.probe.index)
);

const value = computed(() =>
  formatProbeValue(props.probe.type, props.probe.filteredValue)
);

const hiddenForHivernage = computed(
  () => isHivernage(pool) && isProbeHiddenForHivernage(props.probe.type)
);

const probeType = computed(() => getProbeType(props.probe.type));

const outModeDef = computed(() =>
  props.out ? getActiveMode(props.out, pool) : undefined
);

const showUnit = computed(
  () => !shouldHideProbeValueUnit(props.probe.type, props.probe.filteredValue)
);
</script>

<template>
  <div
    v-if="span === 2"
    class="card col-span-2 flex p-4 items-center cursor-pointer"
    :class="{ 'pointer-events-none': hiddenForHivernage }"
    @click="$router.push(`/pool/${$route.params.poolId}/probe/${probe.index}`)"
  >
    <div class="measure-name flex-grow">{{ $t(label) }}</div>
    <div class="text-white text-4xl font-medium" v-if="!unavailable">
      {{ value
      }}<span class="text-sm pl-1" v-if="showUnit">{{ probeType.unit }}</span>
    </div>
    <div class="text-white font-medium" v-else>
      {{ $t("NotAvail") }}
    </div>
  </div>

  <div
    v-else
    class="card text-center p-4 font-medium cursor-pointer"
    :class="{ 'pointer-events-none': hiddenForHivernage }"
    @click="$router.push(`/pool/${$route.params.poolId}/probe/${probe.index}`)"
  >
    <div class="measure-name">{{ $t(label) }}</div>
    <div class="measure-value" v-if="!unavailable && !hiddenForHivernage">
      {{ value
      }}<span class="text-sm pl-1" v-if="showUnit">{{ probeType.unit }}</span>
    </div>
    <div class="text-white font-medium" v-else>
      {{ unavailable ? $t("NotAvail") : $t("RegulHivernage") }}
    </div>

    <div v-if="out && !hiddenForHivernage" class="text-xs">
      <div class="text-sm uppercase mt-4">
        {{ $t(outModeDef?.label ?? "Unknown") }}
      </div>

      <on-off-status
        v-if="outModeDef?.showStatus"
        :out="out"
        class="justify-center"
      />
    </div>
  </div>
</template>
